'use client';

import { createContext, useContext, type ReactNode } from 'react';

import type { EditorType } from '@/types/feedback';

interface WorkbenchConfigContextValue {
  editorType?: EditorType;
  metaSiteId: string;
  feedbackId: string;
  /**
   * Opaque blob minted by the page that rendered this workbench. It carries the
   * feedback/metasite/commenter identity the write routes trust instead of the
   * request body, so it must be sent verbatim and never reconstructed here.
   * Empty on the partner workbench, which does not create or update pins.
   */
  sealedContext?: string;
  sidebarReadOnly: boolean;
  /**
   * The agency that owns the site: the name every reply by a Wix user (the
   * agency or a collaborator) is shown under.
   */
  agencyName?: string;
}

const WorkbenchConfigContext = createContext<WorkbenchConfigContextValue>({
  metaSiteId: '',
  feedbackId: '',
  sealedContext: '',
  sidebarReadOnly: false,
});

export function WorkbenchConfigProvider({
  editorType,
  metaSiteId,
  feedbackId,
  sealedContext,
  sidebarReadOnly,
  agencyName,
  children,
}: WorkbenchConfigContextValue & { children: ReactNode }) {
  return (
    <WorkbenchConfigContext
      value={{
        editorType,
        metaSiteId,
        feedbackId,
        sealedContext: sealedContext ?? '',
        sidebarReadOnly,
        agencyName,
      }}
    >
      {children}
    </WorkbenchConfigContext>
  );
}

export function useWorkbenchConfig() {
  return useContext(WorkbenchConfigContext);
}
